/**
 * PF-3a — reading an operator's ILI anomaly list (CSV, pasted text or XLSX) into
 * the rows POST /pipeline-facility/anomalies/import/confirm takes.
 *
 * STRUCTURED ONLY. A header row names the columns; this file maps the names an
 * ILI vendor list commonly uses onto the API's keys. The AI parse of a PDF or a
 * free-form sheet is PF-3b and is not here.
 *
 * NOTHING IS GUESSED.
 *  - A feature type that is not one of the API's five (after the aliases below)
 *    is a row error, never `other`.
 *  - A header that NAMES a unit (`Chainage (ft)`) must name the tenant's unit.
 *    A mismatch is refused, not converted: the API reads every length in the
 *    tenant's units, and a silent conversion here would be a second answer.
 *  - A blank cell sends nothing (absent), never 0.
 *  - Every row error is collected, not just the first, so the review table can
 *    show all of them at once.
 *
 * The API re-validates everything (all-or-nothing, a 400 naming rows[i].field)
 * and dedupes against the site's list; this file only shapes the request.
 */
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { PfAnomalyImportRow } from '../types/pf-digs';

export const PF_FEATURE_TYPE_VALUES = ['metal_loss', 'dent', 'crack_like', 'girth_weld', 'other'] as const;

/** The API key each header names. Headers are folded (lower case, unit in brackets removed, punctuation → space). */
const HEADER_ALIASES: Record<keyof PfAnomalyImportRow, string[]> = {
  feature_id: ['feature id', 'feature', 'feature no', 'feature number', 'feature', 'anomaly id', 'id', 'ref', 'reference'],
  feature_type: ['feature type', 'type', 'anomaly type', 'classification', 'class', 'identification'],
  chainage: ['chainage', 'kp', 'station'],
  odometer: ['odometer', 'log distance', 'absolute distance', 'distance'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'long', 'lon', 'lng'],
  clock_position: ['clock position', 'clock', 'orientation', 'o clock'],
  depth_pct_wt: ['depth pct wt', 'depth', 'depth pct', 'depth wt', 'peak depth', 'max depth', 'depth percent'],
  length: ['length', 'axial length'],
  width: ['width', 'circumferential width', 'circ width'],
  ili_vendor: ['ili vendor', 'vendor'],
  ili_run_date: ['ili run date', 'run date', 'inspection date'],
  priority: ['priority', 'dig priority'],
  notes: ['notes', 'comments', 'comment', 'remarks'],
  segment_id: [],
};

/** Value aliases for feature type, folded the same way. */
const TYPE_ALIASES: Record<string, (typeof PF_FEATURE_TYPE_VALUES)[number]> = {
  'metal loss': 'metal_loss',
  'metal_loss': 'metal_loss',
  ml: 'metal_loss',
  corrosion: 'metal_loss',
  'external metal loss': 'metal_loss',
  'internal metal loss': 'metal_loss',
  dent: 'dent',
  crack: 'crack_like',
  'crack like': 'crack_like',
  crack_like: 'crack_like',
  'girth weld': 'girth_weld',
  girth_weld: 'girth_weld',
  'girth weld anomaly': 'girth_weld',
  gwa: 'girth_weld',
  other: 'other',
};

/** Which unit a column is read in: chainage-kind (tenant chainage unit), wt-kind (tenant WT unit) or percent. */
const UNIT_KIND: Partial<Record<keyof PfAnomalyImportRow, 'chainage' | 'wt' | 'percent'>> = {
  chainage: 'chainage',
  odometer: 'chainage',
  length: 'wt',
  width: 'wt',
  depth_pct_wt: 'percent',
};

const NUMBER_KEYS = new Set<keyof PfAnomalyImportRow>(['chainage', 'odometer', 'latitude', 'longitude', 'depth_pct_wt', 'length', 'width']);

export interface PfImportUnits {
  chainage: string | null;
  wallThickness: string | null;
}

export interface PfParsedRow {
  /** 1-based line in the sheet (the header is line 1). */
  line: number;
  /** The request row, or null when it has errors. */
  row: PfAnomalyImportRow | null;
  /** What the person typed, by API key — shown in the review table. */
  cells: Partial<Record<keyof PfAnomalyImportRow, string>>;
  errors: string[];
}

export interface PfParsedSheet {
  /** Problems with the sheet as a whole (no header, a wrong unit). Nothing can be confirmed while any exist. */
  sheetErrors: string[];
  /** Columns that were read, by API key → the header as written. */
  columns: Partial<Record<keyof PfAnomalyImportRow, string>>;
  /** Headers that matched nothing, so the person sees what was left out. */
  ignored: string[];
  rows: PfParsedRow[];
}

const fold = (s: string) =>
  s
    .toLowerCase()
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
    .replace(/%/g, ' pct ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** The unit a header names in brackets — `Chainage (m)` → `m`; none → null. */
function headerUnit(h: string): string | null {
  const m = h.match(/[([]\s*([^)\]]+?)\s*[)\]]/);
  return m ? m[1].trim().toLowerCase() : null;
}

const unitMatches = (named: string, tenant: string | null) =>
  !!tenant && named.replace(/\s+/g, '').replace(/\.$/, '') === tenant.toLowerCase().replace(/\s+/g, '');

function bindHeaders(headers: string[], units: PfImportUnits) {
  const columns: Partial<Record<keyof PfAnomalyImportRow, string>> = {};
  const index: Partial<Record<keyof PfAnomalyImportRow, number>> = {};
  const ignored: string[] = [];
  const sheetErrors: string[] = [];
  headers.forEach((raw, i) => {
    const h = String(raw ?? '').trim();
    if (!h) return;
    const f = fold(h);
    const key = (Object.keys(HEADER_ALIASES) as (keyof PfAnomalyImportRow)[]).find(
      (k) => !(k in index) && HEADER_ALIASES[k].includes(f),
    );
    if (!key) {
      ignored.push(h);
      return;
    }
    index[key] = i;
    columns[key] = h;
    const kind = UNIT_KIND[key];
    const named = headerUnit(h);
    if (kind && named) {
      if (kind === 'percent') {
        if (!/^%|^pct|^percent|^%\s*wt|^% ?w\.?t\.?$/.test(named)) {
          sheetErrors.push(`Column "${h}" must be depth as a percent of wall thickness.`);
        }
      } else {
        const tenant = kind === 'chainage' ? units.chainage : units.wallThickness;
        if (!unitMatches(named, tenant)) {
          sheetErrors.push(
            tenant
              ? `Column "${h}" is in ${named}, but this company records ${kind === 'chainage' ? 'chainage' : 'lengths'} in ${tenant}. Convert the column to ${tenant} before importing.`
              : `Column "${h}" names a unit (${named}), but the company's units are not known. Reload the page.`,
          );
        }
      }
    }
  });
  if (!('feature_id' in index)) sheetErrors.push('No feature ID column was found. Name a column "Feature ID".');
  if (!('feature_type' in index)) sheetErrors.push('No feature type column was found. Name a column "Feature type".');
  return { columns, index, ignored, sheetErrors };
}

function readCell(key: keyof PfAnomalyImportRow, raw: string, errors: string[], label: string): unknown {
  const v = raw.trim();
  if (v === '') return undefined;
  if (key === 'feature_type') {
    const t = TYPE_ALIASES[fold(v)] ?? TYPE_ALIASES[v.toLowerCase()];
    if (!t) {
      errors.push(`${label}: "${v}" is not a feature type (metal loss, dent, crack-like, girth weld or other).`);
      return undefined;
    }
    return t;
  }
  if (NUMBER_KEYS.has(key)) {
    const n = Number(v.replace(/,/g, '').replace(/%$/, ''));
    if (!Number.isFinite(n)) {
      errors.push(`${label}: "${v}" is not a number.`);
      return undefined;
    }
    if (key === 'depth_pct_wt' && (n < 0 || n > 100)) errors.push(`${label}: depth must be between 0 and 100 %.`);
    if ((key === 'length' || key === 'width') && n <= 0) errors.push(`${label}: must be greater than 0.`);
    if ((key === 'chainage' || key === 'odometer') && n < 0) errors.push(`${label}: cannot be negative.`);
    if (key === 'latitude' && (n < -90 || n > 90)) errors.push(`${label}: must be between -90 and 90.`);
    if (key === 'longitude' && (n < -180 || n > 180)) errors.push(`${label}: must be between -180 and 180.`);
    return n;
  }
  if (key === 'clock_position') {
    const t = v.replace(/\s*(am|pm)$/i, '').replace(/^0(?=\d:)/, '').replace(/:(\d\d):\d\d$/, ':$1');
    if (!/^(?:[1-9]|1[0-2]):[0-5]\d$/.test(t)) {
      errors.push(`${label}: "${v}" is not a clock position like 3:30 (1:00 to 12:59).`);
      return undefined;
    }
    return t;
  }
  if (key === 'ili_run_date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      errors.push(`${label}: "${v}" is not a date as YYYY-MM-DD.`);
      return undefined;
    }
    return v;
  }
  return v;
}

/** Bind a grid (first non-empty row = header) into request rows. */
export function parseAnomalyGrid(grid: string[][], units: PfImportUnits): PfParsedSheet {
  const firstRow = grid.findIndex((r) => r.some((c) => String(c ?? '').trim() !== ''));
  if (firstRow < 0) return { sheetErrors: ['The sheet is empty.'], columns: {}, ignored: [], rows: [] };
  const { columns, index, ignored, sheetErrors } = bindHeaders(grid[firstRow].map((c) => String(c ?? '')), units);
  const rows: PfParsedRow[] = [];
  for (let r = firstRow + 1; r < grid.length; r++) {
    const line = grid[r].map((c) => String(c ?? ''));
    if (line.every((c) => c.trim() === '')) continue;
    const errors: string[] = [];
    const cells: PfParsedRow['cells'] = {};
    const body: Record<string, unknown> = {};
    (Object.keys(index) as (keyof PfAnomalyImportRow)[]).forEach((key) => {
      const raw = line[index[key] as number] ?? '';
      cells[key] = raw.trim();
      const v = readCell(key, raw, errors, columns[key] as string);
      if (v !== undefined) body[key] = v;
    });
    if (!body.feature_id && !errors.some((e) => e.startsWith(`${columns.feature_id}:`))) errors.push('Feature ID is blank.');
    if (!body.feature_type && cells.feature_type === '') errors.push('Feature type is blank.');
    rows.push({ line: r + 1, row: errors.length ? null : (body as unknown as PfAnomalyImportRow), cells, errors });
  }
  if (!rows.length && !sheetErrors.length) sheetErrors.push('The sheet has a header row but no anomalies under it.');
  return { sheetErrors, columns, ignored, rows };
}

/** CSV or pasted text (comma- or tab-separated — Papa detects which). */
export function parseAnomalyText(text: string, units: PfImportUnits): PfParsedSheet {
  const out = Papa.parse<string[]>(text.replace(/^﻿/, ''), { skipEmptyLines: false });
  return parseAnomalyGrid(out.data as string[][], units);
}

/** The first sheet of an XLSX workbook. Dates read as YYYY-MM-DD; numbers as the cell shows them. */
export function parseAnomalyWorkbook(buf: ArrayBuffer, units: PfImportUnits): PfParsedSheet {
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const first = wb.SheetNames[0];
  if (!first) return { sheetErrors: ['The workbook has no sheets.'], columns: {}, ignored: [], rows: [] };
  const grid = XLSX.utils.sheet_to_json<string[]>(wb.Sheets[first], { header: 1, raw: false, defval: '', dateNF: 'yyyy-mm-dd' });
  return parseAnomalyGrid(grid as string[][], units);
}

/** Every row confirmable? (No sheet errors, no row errors, at least one row.) */
export const sheetConfirmable = (s: PfParsedSheet | null) =>
  !!s && s.sheetErrors.length === 0 && s.rows.length > 0 && s.rows.every((r) => r.errors.length === 0);

/** The import result as one plain sentence. Inserted 0 with skips is said outright, so nobody clicks again. */
export function importResultSentence(counts: { inserted: number; skipped: number }): string {
  const { inserted, skipped } = counts;
  if (inserted === 0 && skipped > 0) {
    return skipped === 1
      ? "Nothing was added. The 1 row was already on this site's anomaly list or repeated in the file, so it was skipped."
      : `Nothing was added. All ${skipped} rows were already on this site's anomaly list or repeated in the file, so they were skipped.`;
  }
  const a = `${inserted} anomal${inserted === 1 ? 'y' : 'ies'} added`;
  return skipped > 0 ? `${a}. ${skipped} row${skipped === 1 ? ' was' : 's were'} skipped — see below.` : `${a}.`;
}

export const SKIP_REASON_LABEL: Record<string, string> = {
  duplicate: 'Already on the list',
  feature_id_conflict: 'Same feature ID at a different position',
  duplicate_in_batch: 'Repeated in this file',
};
